import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { trackPurchase } from '@/hooks/useAnalytics';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const isGuest = searchParams.get('guest') === 'true';
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const { clearCart } = useCart();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const [orderId, setOrderId] = useState(null);
  const [accountCreated, setAccountCreated] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestPassword, setGuestPassword] = useState('');
  const maxAttempts = 5;

  useEffect(() => {
    if (!sessionId) {
      navigate('/cart');
      return;
    }
    checkPaymentStatus();
  }, [sessionId, attempts]);

  const checkPaymentStatus = async () => {
    try {
      let response;
      if (isGuest || !isAuthenticated) {
        response = await axios.get(`${API}/checkout/guest-status/${sessionId}`);
      } else {
        response = await axios.get(
          `${API}/checkout/status/${sessionId}`,
          getAuthHeaders()
        );
      }

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setOrderId(response.data.order_id);
        clearCart();
        trackPurchase();
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else if (attempts < maxAttempts) {
        setTimeout(() => setAttempts(attempts + 1), 2000);
      } else {
        setStatus('timeout');
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
      setStatus('error');
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!guestName.trim() || !guestPassword.trim()) {
      toast.error('Please fill in all fields');
      return;
    }
    if (guestPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setCreatingAccount(true);
    try {
      const response = await axios.post(`${API}/auth/register-guest`, {
        order_id: orderId,
        password: guestPassword,
        name: guestName
      });

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setAccountCreated(true);
      toast.success(`Welcome, ${user.name}! Your account has been created.`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to create account';
      toast.error(msg);
    } finally {
      setCreatingAccount(false);
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center" data-testid="checkout-success-page">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-accent" />
          <p className="text-lg text-muted-foreground">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center" data-testid="checkout-success-page">
        <div className="text-center max-w-2xl px-6">
          <CheckCircle2 className="h-20 w-20 text-accent mx-auto mb-6" data-testid="success-icon" />
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-4" data-testid="success-title">
            Order Confirmed!
          </h1>
          <p className="text-base md:text-lg text-muted-foreground mb-8">
            Thank you for your purchase. Your order has been confirmed and will be shipped soon.
            You will receive an email with tracking information.
          </p>

          {/* Guest account creation */}
          {isGuest && !accountCreated && !isAuthenticated && orderId && (
            <div className="border border-border rounded-lg p-8 mb-8 text-left max-w-md mx-auto" data-testid="guest-register-section">
              <div className="flex items-center gap-3 mb-4">
                <UserPlus className="h-5 w-5" />
                <h2 className="text-sm uppercase tracking-[0.15em] font-medium">Create an Account</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Save your order history and track shipments by creating a free account.
              </p>
              <form onSubmit={handleCreateAccount} className="space-y-4">
                <div>
                  <Label htmlFor="guest-name">YOUR NAME</Label>
                  <Input
                    id="guest-name"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="Full name"
                    required
                    data-testid="guest-register-name"
                  />
                </div>
                <div>
                  <Label htmlFor="guest-password">CREATE PASSWORD</Label>
                  <Input
                    id="guest-password"
                    type="password"
                    value={guestPassword}
                    onChange={(e) => setGuestPassword(e.target.value)}
                    placeholder="Min. 6 characters"
                    required
                    minLength={6}
                    data-testid="guest-register-password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={creatingAccount}
                  className="w-full rounded-full text-sm uppercase tracking-widest"
                  data-testid="guest-register-submit"
                >
                  {creatingAccount ? 'CREATING...' : 'CREATE ACCOUNT'}
                </Button>
              </form>
            </div>
          )}

          {/* Account created confirmation */}
          {accountCreated && (
            <div className="border border-accent/30 bg-accent/5 rounded-lg p-6 mb-8 max-w-md mx-auto" data-testid="account-created-section">
              <CheckCircle2 className="h-6 w-6 text-accent mx-auto mb-2" />
              <p className="text-sm font-medium">Account created! You can now track your orders.</p>
            </div>
          )}

          <div className="flex gap-4 justify-center flex-wrap">
            {(isAuthenticated || accountCreated) ? (
              <Link to="/dashboard">
                <Button size="lg" className="rounded-full" data-testid="view-orders-btn">
                  VIEW MY ORDERS
                </Button>
              </Link>
            ) : (
              <Link to="/gallery">
                <Button size="lg" className="rounded-full" data-testid="back-to-gallery-btn">
                  BACK TO GALLERY
                </Button>
              </Link>
            )}
            <Link to="/gallery">
              <Button size="lg" variant="outline" className="rounded-full" data-testid="continue-shopping-success-btn">
                CONTINUE SHOPPING
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-24 flex items-center justify-center">
      <div className="text-center max-w-2xl px-6">
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight mb-4">
          Payment Status Unclear
        </h1>
        <p className="text-base md:text-lg text-muted-foreground mb-8">
          We're still processing your payment. Please check your email for confirmation.
        </p>
        <Link to="/gallery">
          <Button size="lg" className="rounded-full">
            BACK TO GALLERY
          </Button>
        </Link>
      </div>
    </div>
  );
}
